// src/contexts/LoginContext.jsx
import { createContext, useContext, useEffect, useState } from 'react';
import config from '../../config';
import { getCookie, isInWebView } from '../utils';

export const LoginContext = createContext();

export const LoginProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    async function init() {
      await fetch(`${config.apiBaseUrl}/api/get-csrf/`, {
        credentials: 'include',
      });

      const savedUser = localStorage.getItem('authUser');
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsLoggedIn(true);
      }

      try {
        const res = await fetch(`${config.apiBaseUrl}/api/current-user/`, {
          credentials: 'include',
          headers: { 'X-CSRFToken': getCookie('csrftoken') },
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
          setIsLoggedIn(true);
          localStorage.setItem('authUser', JSON.stringify(data));
        } else {
          setIsLoggedIn(false);
          localStorage.removeItem('authUser');
        }
      } catch (e) {
        console.error('login check failed', e);
      }
    }
    init();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const userStr = params.get('user');
    if (userStr) {
      try {
        const decoded = JSON.parse(atob(userStr));
        setUser(decoded);
        setIsLoggedIn(true);
        localStorage.setItem('authUser', JSON.stringify(decoded));
        params.delete('user');
        window.history.replaceState({}, '', window.location.pathname);
      } catch {}
    }
  }, []);

  const handleLoginOrRegister = async () => {
    const url = loginMode === 'login'
      ? `${config.apiBaseUrl}/api/login/`
      : `${config.apiBaseUrl}/api/register/`;

    const payload = loginMode === 'login'
      ? { email, password }
      : { email, password, confirmPassword };

    try {
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        alert(errorData.detail || 'Login failed');
        return;
      }

      const data = await res.json();
      setUser(data.user);
      setIsLoggedIn(true);
      localStorage.setItem('authUser', JSON.stringify(data.user));
      setShowLoginModal(false);
    } catch (err) {
      alert('Network error');
    }
  };

  const handleGoogleLogin = () => {
    const loginUrl = `${config.apiBaseUrl}/api/google-login/`;

    if (isInWebView()) {
      const confirmExternal = window.confirm('Login with Google in external browser?');
      if (confirmExternal) {
        window.open(loginUrl, '_blank');
      }
    } else {
      window.location.href = loginUrl;
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    localStorage.removeItem('authUser');
    setUser(null);
    setIsLoggedIn(false);

    try {
      await fetch(`${config.apiBaseUrl}/api/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'),
        },
      });
    } catch (error) {
      console.error('登出失败（后端接口未响应）:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <LoginContext.Provider
      value={{
        user,
        setUser,
        isLoggedIn,
        showLoginModal,
        setShowLoginModal,
        loginMode,
        setLoginMode,
        email,
        setEmail,
        password,
        setPassword,
        confirmPassword,
        setConfirmPassword,
        handleLoginOrRegister,
        handleGoogleLogin,
        handleLogout,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};

export const useLogin = () => useContext(LoginContext);